import React, { useState, useEffect, useRef } from 'react';
import { useAppAuth } from '../context/AuthContext';
import { usePanel } from '../context/PanelContext';
import { useNotification } from '../context/NotificationContext';
import './ThumbnailTestsPage.css';

// Compress an image file to JPEG at target max dimension and quality
function compressImage(file, maxDim = 1280, quality = 0.8) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > maxDim || height > maxDim) {
          const ratio = Math.min(maxDim / width, maxDim / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', quality));
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function ThumbnailTestsPage() {
  const { getToken } = useAppAuth();
  const { notify } = useNotification();
  const { thumbTests: tests, thumbLoading: loading, thumbAuthFetch: authFetch, refreshThumbTests: fetchTests } = usePanel();
  const [showCreate, setShowCreate] = useState(false);
  const [title, setTitle] = useState('');
  const [images, setImages] = useState([]);
  const [creating, setCreating] = useState(false);
  const [expandedVotes, setExpandedVotes] = useState(null);
  const [votes, setVotes] = useState([]);
  const [copiedSlug, setCopiedSlug] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchTests();
  }, [fetchTests]);

  const handleFiles = async (files) => {
    const fileList = Array.from(files).slice(0, 3 - images.length);
    const compressed = await Promise.all(fileList.map(f => compressImage(f)));
    setImages(prev => [...prev, ...compressed].slice(0, 3));
  };

  const handleCreate = async () => {
    if (!title.trim() || images.length < 2) return;
    setCreating(true);
    try {
      const data = await authFetch('POST', { title: title.trim(), images });
      notify('Test created');
      setShowCreate(false);
      setTitle('');
      setImages([]);
      await fetchTests();
    } catch (err) {
      notify(err.message);
    }
    setCreating(false);
  };

  const toggleVotes = async (slug) => {
    if (expandedVotes === slug) {
      setExpandedVotes(null);
      setVotes([]);
      return;
    }
    try {
      const token = await getToken();
      const res = await fetch(`/api/thumbnail-tests?action=votes&slug=${encodeURIComponent(slug)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setVotes(data.votes || []);
      setExpandedVotes(slug);
    } catch {}
  };

  const deleteVote = async (voteId, slug) => {
    const token = await getToken();
    await fetch('/api/thumbnail-tests?action=delete-vote', {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ id: voteId }),
    });
    // Refresh votes list
    const res = await fetch(`/api/thumbnail-tests?action=votes&slug=${encodeURIComponent(slug)}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await res.json();
    setVotes(data.votes || []);
    await fetchTests();
    notify('Vote deleted');
  };

  const handleClose = async (slug, currentStatus) => {
    const newStatus = currentStatus === 'open' ? 'closed' : 'open';
    try {
      await authFetch('PUT', { slug, status: newStatus });
      await fetchTests();
    } catch (err) {
      notify(err.message);
    }
  };

  const handleDelete = async (slug) => {
    try {
      await authFetch('DELETE', { slug });
      notify('Test deleted');
      await fetchTests();
    } catch (err) {
      notify(err.message);
    }
  };

  const copyLink = (slug) => {
    navigator.clipboard.writeText(`${window.location.origin}/vote/${slug}`);
    setCopiedSlug(slug);
    setTimeout(() => setCopiedSlug(null), 2000);
  };

  return (
    <div className="tt-page">
      <div className="tt-container">
        <div className="tt-header">
          <h2 className="tt-title">Thumbnails</h2>
          <button className="tt-create-btn" onClick={() => setShowCreate(true)}>
            + New Test
          </button>
        </div>

        {loading ? (
          <div className="tt-loading">Loading...</div>
        ) : tests.length === 0 ? (
          <div className="tt-empty">No thumbnail tests yet. Create one to get started.</div>
        ) : (
          <div className="tt-list">
            {tests.map(test => {
              const maxScore = Math.max(...(test.scores || [0]));
              return (
                <div className="tt-card" key={test.slug}>
                  <div className="tt-card-header">
                    <div className="tt-card-info">
                      <span className="tt-card-title">{test.title}</span>
                      <span className={`tt-status tt-status-${test.status}`}>{test.status}</span>
                    </div>
                    <span className="tt-card-votes">{test.totalVotes} vote{test.totalVotes !== 1 ? 's' : ''}</span>
                  </div>

                  <div className="tt-results">
                    {test.images.map((img, i) => {
                      const score = (test.scores || [])[i] || 0;
                      const firstPlace = (test.firstPlaceCounts || [])[i] || 0;
                      const pct = maxScore > 0 ? (score / maxScore) * 100 : 0;
                      const isWinner = score === maxScore && maxScore > 0;
                      return (
                        <div className={`tt-result${isWinner ? ' tt-result-winner' : ''}`} key={i}>
                          <img src={img} alt={`Option ${i + 1}`} className="tt-result-img" />
                          <div className="tt-result-data">
                            <div className="tt-result-scores">
                              <span className="tt-result-score">{score} pts</span>
                              <span className="tt-result-first">{firstPlace} first-place</span>
                            </div>
                            <div className="tt-bar-track">
                              <div className="tt-bar-fill" style={{ width: `${pct}%` }} />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="tt-card-actions">
                    <button className="tt-action-btn" onClick={() => copyLink(test.slug)}>
                      {copiedSlug === test.slug ? 'Copied!' : 'Copy Link'}
                    </button>
                    <button className="tt-action-btn" onClick={() => handleClose(test.slug, test.status)}>
                      {test.status === 'open' ? 'Close' : 'Reopen'}
                    </button>
                    <button className="tt-action-btn tt-action-danger" onClick={() => handleDelete(test.slug)}>
                      Delete
                    </button>
                    <button className="tt-action-btn" onClick={() => toggleVotes(test.slug)}>
                      {expandedVotes === test.slug ? 'Hide Votes' : 'Votes'}
                    </button>
                  </div>

                  {expandedVotes === test.slug && (
                    <div className="tt-votes-list">
                      {votes.length === 0 ? (
                        <div className="tt-votes-empty">No votes yet</div>
                      ) : votes.map(v => (
                        <div className="tt-vote-row" key={v.id}>
                          <span className="tt-vote-fp">{v.fingerprintId.slice(0, 12)}...</span>
                          <span className="tt-vote-ranking">
                            {v.ranking.map((imgIdx, rank) => (
                              <span key={rank} className="tt-vote-rank-item">
                                #{rank + 1}: Img {imgIdx + 1}
                              </span>
                            ))}
                          </span>
                          <span className="tt-vote-date">
                            {new Date(v.votedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                          </span>
                          <button className="tt-action-btn tt-action-danger tt-vote-delete" onClick={() => deleteVote(v.id, test.slug)}>
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Create Modal */}
        {showCreate && (
          <div className="tt-modal-overlay" onClick={() => setShowCreate(false)}>
            <div className="tt-modal" onClick={e => e.stopPropagation()}>
              <h3 className="tt-modal-title">New Thumbnail Test</h3>

              <label className="tt-label">Title</label>
              <input
                className="tt-input"
                type="text"
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder="Which thumbnail is best?"
                maxLength={200}
              />

              <label className="tt-label">Thumbnails ({images.length}/3, min 2)</label>
              <div className="tt-upload-area" onClick={() => fileInputRef.current?.click()}>
                {images.length === 0 ? (
                  <span className="tt-upload-text">Click to upload images</span>
                ) : (
                  <div className="tt-upload-previews">
                    {images.map((img, i) => (
                      <div className="tt-upload-preview" key={i}>
                        <img src={img} alt={`Preview ${i + 1}`} />
                        <button
                          className="tt-upload-remove"
                          onClick={(e) => {
                            e.stopPropagation();
                            setImages(prev => prev.filter((_, idx) => idx !== i));
                          }}
                        >
                          &times;
                        </button>
                      </div>
                    ))}
                    {images.length < 3 && (
                      <div className="tt-upload-add">+</div>
                    )}
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  style={{ display: 'none' }}
                  onChange={e => { handleFiles(e.target.files); e.target.value = ''; }}
                />
              </div>

              <div className="tt-modal-actions">
                <button className="tt-action-btn" onClick={() => setShowCreate(false)}>Cancel</button>
                <button
                  className="tt-create-submit"
                  onClick={handleCreate}
                  disabled={creating || !title.trim() || images.length < 2}
                >
                  {creating ? 'Creating...' : 'Create Test'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
